import type { EntityFormConfig } from '@components/integrated/entity-form';
import {
  createFormActions,
  createFormEndpoint,
  createFormI18n,
  createFormCreateId,
  createFormEditId,
  createFormLayout,
  createFormWorkflow,
} from './formConfigHelpers';
import { getStepTypeLabel } from '../pages/workflows/step-config/types';

/**
 * Returns the EntityForm configuration for Workflow Step create/edit forms.
 *
 * @param mode - Whether the form is for creating or editing a step.
 * @param workflowSlug - The parent workflow slug (used to build the nested endpoint).
 * @param stepId - The step ID (required in edit mode for fetching data).
 */
export function getWorkflowStepFormConfig(
  mode: 'create' | 'edit',
  workflowSlug: string,
  stepId?: string,
  stepTypes?: string[],
): EntityFormConfig {
  const isEdit = mode === 'edit';

  return {
    id: isEdit
      ? createFormEditId('workflow-step', stepId)
      : createFormCreateId('workflow-step'),

    endpoint: createFormEndpoint(
      isEdit,
      `/api/admin/workflows/${workflowSlug}/steps`,
      `/api/admin/workflows/${workflowSlug}/steps/${stepId}`,
      !!stepId,
      ['name', 'step_type', 'sort_order', 'execution_mode', 'is_active', 'timeout_seconds', 'config_iterate_group', 'config_iterate_over', 'config_iterate_as'],
    ),

    fields: {
      name: {
        type: 'text',
        label: 'Name',
        placeholder: 'e.g. Validate Claim Data',
        order: 1,
        section: 'basic',
        validation: {
          required: true,
          max: { value: 255, message: 'Name must be at most 255 characters' },
        },
      },
      step_type: {
        type: 'select',
        label: 'Step Type',
        helperText: 'The type of action this step performs',
        order: 2,
        section: 'basic',
        options: {
          items: (stepTypes ?? []).map((t) => ({ value: t, label: getStepTypeLabel(t) })),
        },
        validation: {
          required: true,
        },
      },
      sort_order: {
        type: 'number',
        label: 'Sort Order',
        placeholder: 'e.g. 0',
        helperText: 'Execution order (0-based)',
        order: 3,
        section: 'basic',
        validation: {
          required: true,
        },
      },
      execution_mode: {
        type: 'select',
        label: 'Execution Mode',
        order: 4,
        section: 'basic',
        options: {
          items: [
            { value: 'sync', label: 'Synchronous' },
            { value: 'async', label: 'Asynchronous' },
          ],
        },
        defaultValue: 'sync',
      },
      is_active: {
        type: 'checkbox',
        label: 'Active',
        helperText: 'Enable or disable this step',
        order: 5,
        section: 'basic',
      },
      config_iterate_group: {
        type: 'text',
        label: 'Iteration Group',
        placeholder: 'e.g. per_garantie',
        helperText: 'Groups consecutive steps into an iteration block. Steps sharing the same group execute together for each item.',
        order: 1,
        section: 'iteration',
      },
      config_iterate_over: {
        type: 'text',
        label: 'Iterate Over',
        placeholder: 'e.g. codes_garanties',
        helperText: 'Context key pointing to an array to iterate over. Supports dot notation.',
        order: 2,
        section: 'iteration',
      },
      config_iterate_as: {
        type: 'text',
        label: 'Iterate As',
        placeholder: 'e.g. current_code_garantie',
        helperText: 'Context key under which the current iteration item is injected.',
        order: 3,
        section: 'iteration',
      },
      timeout_seconds: {
        type: 'number',
        label: 'Timeout (seconds)',
        placeholder: 'e.g. 30',
        helperText: 'Maximum time for step execution',
        order: 6,
        section: 'advanced',
      },
      condition: {
        type: 'textarea',
        label: 'Condition (JSON)',
        placeholder: '{}',
        helperText: 'JSON condition that must be met for this step to execute',
        order: 7,
        section: 'advanced',
        validation: {
          max: { value: 10000, message: 'Condition must be at most 10000 characters' },
        },
      },
      retry_policy: {
        type: 'textarea',
        label: 'Retry Policy (JSON)',
        placeholder: '{}',
        helperText: 'JSON retry configuration',
        order: 8,
        section: 'advanced',
        validation: {
          max: { value: 10000, message: 'Retry policy must be at most 10000 characters' },
        },
      },
    },

    sections: [
      {
        id: 'basic',
        title: 'Step Information',
        order: 1,
      },
      {
        id: 'iteration',
        title: 'Iteration',
        order: 2,
      },
      {
        id: 'advanced',
        title: 'Advanced',
        order: 3,
      },
    ],

    actions: createFormActions('Step', isEdit, { alignment: 'right' }),

    layout: createFormLayout(),

    workflow: createFormWorkflow(),

    i18n: createFormI18n('Step', isEdit),
  };
}
